/* ─────────────────────────────────────────────────────────────────────────
   RBAC · thin consumer of the server-authoritative permission matrix at
   `GET /api/rbac/matrix` (Batch 2 · PR 5.5).

   Why a thin consumer?  The frontend had a parallel copy of the permission
   matrix that had to be kept in lockstep with `backend/rbac.py` by hand.
   Two prior drift findings traced back to that mirror discipline.  Now the
   server is the single source of truth; the JS file fetches once on app
   boot and `can(role, action)` reads the cached result.

   Operational lever: a permission tightening (server-side perm change)
   ships to production WITHOUT a frontend redeploy — usable during
   incident response when an action needs to be denied immediately.

   Failure mode contract: if the matrix hasn't loaded yet OR the fetch
   failed, `can()` returns `false` for every action.  The user sees no UI
   affordance they can't actually use, and the server still gates the
   request anyway — both layers stay safe by default.

   Visual / label metadata (sub, rank, pdiContribution) lives client-side.
   The server matrix carries only the security-relevant fields (role IDs +
   action → role tuples); display labels are a frontend concern.
   ─────────────────────────────────────────────────────────────────────── */

import { useSyncExternalStore } from "react";
import { api } from "./api";

// ─── Visual / label metadata — frontend-owned, not security-relevant ───────
export const ROLE_REGISTRY = [
  { id: "DIRECTOR",        label: "Director",        sub: "Master Access",          rank: 4, pdiContribution: 96 },
  { id: "MANAGER",         label: "Manager",         sub: "Operational · Approval", rank: 3, pdiContribution: 88 },
  { id: "CATEGORY_LEADER", label: "Category Leader", sub: "Domain Focus",           rank: 2, pdiContribution: 82 },
  { id: "BUYER",           label: "Buyer",           sub: "Execution · AAR",        rank: 1, pdiContribution: 74 },
];
export const ROLE_BY_ID = ROLE_REGISTRY.reduce((acc, r) => ({ ...acc, [r.id]: r }), {});

// ─── Server matrix cache + subscription ────────────────────────────────────
// Module-level mutable state · populated by `loadRbacMatrix()` and read
// synchronously by `can()`.  `null` while not-yet-fetched / failed.
//
// useSyncExternalStore subscription lets gated UI auto-re-render the moment
// the matrix arrives — components that called `can()` and got `false` on
// first mount get a fresh render once the cache fills, so a DIRECTOR doesn't
// briefly see the BUYER view during the bootstrap window.
let _matrix = null;
let _loadPromise = null;
const _listeners = new Set();
function _notify() { for (const l of _listeners) l(); }
function _subscribe(l) { _listeners.add(l); return () => _listeners.delete(l); }
function _getSnapshot() { return _matrix; }

/** Fetch the canonical matrix from the server and cache it.  Idempotent —
 *  concurrent callers share the same in-flight promise so we don't issue
 *  N parallel requests on a busy mount.  Safe to call from anywhere. */
export function loadRbacMatrix() {
  if (_matrix) return Promise.resolve(_matrix);
  if (_loadPromise) return _loadPromise;
  // PR 5.5 hotfix · `api` exports named methods (api.ledger, api.team, …)
  // not a generic .get() axios surface. Call the dedicated rbacMatrix
  // fetcher so the bearer-token interceptor still applies via the shared
  // axios instance.
  _loadPromise = api.rbacMatrix()
    .then((data) => { _matrix = data; _notify(); return _matrix; })
    .catch((err) => {
      // Fail-closed: leave _matrix null so `can()` returns false for
      // everything.  Log so the operator notices in DevTools.
      // eslint-disable-next-line no-console
      console.warn("[rbac] failed to load /api/rbac/matrix · everything denies", err);
      return null;
    })
    .finally(() => { _loadPromise = null; });
  return _loadPromise;
}

/** Synchronous permission check.  Returns `false` if the matrix isn't
 *  loaded yet (fail-closed).  Components that call `can()` from inside a
 *  render and want reactivity should use `useCan(role, action)` below
 *  instead — it subscribes to the matrix cache and re-renders on load. */
export function can(roleId, action) {
  if (!_matrix || !_matrix.permissions) return false;
  const allowed = _matrix.permissions[action];
  if (!allowed) return false;
  return allowed.includes(roleId);
}

/** React hook · same shape as `can()` but reactive to matrix load events.
 *  Use this in components rendering RBAC-gated affordances so a DIRECTOR
 *  view doesn't flash as a BUYER view during the bootstrap fetch. */
export function useCan(roleId, action) {
  const matrix = useSyncExternalStore(_subscribe, _getSnapshot, _getSnapshot);
  if (!matrix || !matrix.permissions) return false;
  const allowed = matrix.permissions[action];
  if (!allowed) return false;
  return allowed.includes(roleId);
}

export function actingAs(operative) {
  const r = ROLE_BY_ID[operative?.role] || ROLE_REGISTRY[3];
  return { ...operative, roleDetail: r };
}

// Re-export the API-backed hooks so existing imports continue to resolve.
export { useCurrentOperative, useTeam } from "./teamStore";
